import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiSun, FiBriefcase, FiLayers, FiTrendingUp, FiCheckCircle, 
  FiMapPin, FiCalendar, FiZap, FiArrowRight, 
  FiShield, FiHeart, FiActivity, FiCpu, 
  FiHome, FiBattery, FiSliders, FiMaximize, FiX, FiSearch,
  FiAlertTriangle, FiLoader
} from 'react-icons/fi';

import { fetchPublicProjects } from '../Services/userServices';

// ==========================================
// STATIC SECTION DATA
// ==========================================
const categories = [
  { id: 'residential', title: 'Residential Solar', icon: FiHome, desc: 'Tailored clean energy systems for modern homes & villas.' },
  { id: 'commercial', title: 'Commercial Solar', icon: FiBriefcase, desc: 'Optimized corporate energy solutions to cut operational tariffs.' },
  { id: 'industrial', title: 'Industrial Solar', icon: FiLayers, desc: 'Robust infrastructure deployments designed for heavy manufacturing.' },
  { id: 'agriculture', title: 'Agricultural Solar', icon: FiSun, desc: 'Smart agrivoltaics empowering irrigation and green farms.' },
];

const timelineSteps = [
  { step: '01', title: 'Site Survey', desc: 'Drone shading mapping.' },
  { step: '02', title: '3D Design', desc: 'Precision architecture.' },
  { step: '03', title: 'Approvals', desc: 'Discom interconnection.' },
  { step: '04', title: 'Installation', desc: 'Certified deployments.' },
  { step: '05', title: 'Testing QA', desc: 'Component validation.' },
  { step: '06', title: 'Grid Sync', desc: 'Live clean generation.' },
];

const technologies = [
  { name: 'Monocrystalline Bi-facial', icon: FiSun, desc: 'Highest premium space efficiency and dual-surface generation.' },
  { name: 'TOPCon High-Density', icon: FiLayers, desc: 'Next-gen solar cells delivering maximum output in low-light.' },
  { name: 'Smart Multi-MPPT Inverters', icon: FiCpu, desc: 'Intelligent switching for handling grids and power backups.' },
  { name: 'Bi-directional Net Metering', icon: FiZap, desc: 'Direct seamless connection to local utilities for export credits.' },
  { name: 'LFP Battery Storage Matrix', icon: FiBattery, desc: 'Ultra-long cycle life power reserves for blackout defense.' },
  { name: 'SCADA Remote Telemetry', icon: FiActivity, desc: '24/7 web-portal analytics tracking module-level generation.' },
];

export default function ProjectsPage() {
  const [projectsList, setProjectsList] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // 🚀 Fetch Public Projects
  useEffect(() => {
    const loadProjects = async () => {
      setIsLoading(true);
      setErrorMessage('');
      const result = await fetchPublicProjects();
      if (result.success) {
        setProjectsList(result.data || []);
      } else {
        setErrorMessage(result.error || 'Failed to load projects.');
      }
      setIsLoading(false);
    };
    loadProjects();
  }, []);

  // 🔍 Filter Logic
  const filteredProjects = projectsList.filter((project) => {
    const matchesFilter = activeFilter === 'all' || project.category === activeFilter;
    const matchesSearch = 
      project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.customer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.tag?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen text-slate-800 font-sans antialiased overflow-x-hidden selection:bg-[#F7941D]/20 selection:text-[#0D3F7F] bg-[#F8FAFC]">
      
      {/* 🌟 1. DARK COMPACT HERO & SECTORS 🌟 */}
      <section className="relative pt-24 pb-12 sm:pt-28 sm:pb-16 lg:pt-36 lg:pb-20 bg-[#050B14] border-b border-[#1E5CB3]/20 z-10 overflow-hidden">
        {/* Glows */}
        <div className="absolute top-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/15 rounded-full blur-[80px] sm:blur-[130px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#1E5CB3]/15 rounded-full blur-[80px] sm:blur-[130px] pointer-events-none" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`, backgroundSize: '30px 30px' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-[#0D3F7F]/40 border border-[#1E5CB3]/40 px-3 py-1 rounded-full mb-3 sm:mb-4 backdrop-blur-md">
              <FiZap className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.15em] text-[#F0F7FF] uppercase">
                Active Portfolio
              </span>
            </div>
            <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white mb-3 sm:mb-4 leading-tight">
              Engineered Solutions for <br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent">
                Every Scale
              </span>
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm md:text-[14px] font-medium leading-relaxed max-w-2xl mx-auto px-2 sm:px-0">
              Explore our completed and active solar installations. From smart residential villas to massive megawatt industrial parks.
            </p>
          </div>

          {/* Compact Sectors Cards */}
          <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {categories.map((cat, i) => (
              <motion.div 
                key={cat.id}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#F7941D]/40 transition-all duration-300 group flex flex-col justify-between backdrop-blur-sm cursor-pointer"
                onClick={() => {
                  setActiveFilter(cat.id);
                  document.getElementById('project-showcase')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div>
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#0D3F7F]/50 text-[#FFC107] flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[#F7941D] group-hover:text-white transition-colors duration-300 shadow-sm">
                    <cat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <h3 className="text-sm sm:text-[15px] font-bold text-white mb-1 sm:mb-1.5">{cat.title}</h3>
                  <p className="text-[11px] sm:text-[12px] text-slate-400 leading-relaxed font-medium">{cat.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 🌟 2. MAIN PROJECT SHOWCASE SECTION 🌟 */}
      <section id="project-showcase" className="py-10 sm:py-12 lg:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* FILTERS & SEARCH BAR (Fully Responsive) */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 sm:gap-5 mb-8 sm:mb-10 bg-white p-3 sm:p-4 rounded-2xl shadow-sm border border-slate-200/60">
          
          {/* Filter Pills — Horizontal scroll on mobile */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar lg:flex-wrap -mx-1 px-1">
            {['all', 'residential', 'commercial', 'industrial', 'agriculture'].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-3.5 sm:px-4 py-2 rounded-full text-[10px] sm:text-[11px] font-bold transition-all uppercase tracking-wider cursor-pointer border shrink-0 touch-manipulation ${
                  activeFilter === filter 
                    ? 'bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white border-transparent shadow-md' 
                    : 'bg-[#F8FAFC] text-slate-500 border-slate-200 hover:border-[#0D3F7F]/30 hover:text-[#0D3F7F]'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full lg:w-72 shrink-0">
            <FiSearch className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search city, client..."
              className="w-full pl-10 pr-4 py-2.5 bg-[#F8FAFC] text-[#0F172A] border border-slate-200 rounded-full focus:border-[#F7941D] focus:ring-2 focus:ring-[#F7941D]/10 outline-none text-xs sm:text-[12px] font-bold transition-all placeholder-slate-400"
            />
          </div>
        </div>

        {/* 🌟 DYNAMIC STATE HANDLERS 🌟 */}
        {isLoading ? (
          <div className="py-16 sm:py-20 flex flex-col items-center justify-center text-center">
            <FiLoader className="w-7 h-7 sm:w-8 sm:h-8 text-[#F7941D] animate-spin mb-3" />
            <p className="text-xs sm:text-[14px] font-bold text-[#0D3F7F]">Loading Database...</p>
          </div>
        ) : errorMessage ? (
          <div className="p-8 sm:p-10 text-center bg-red-50 rounded-2xl border border-red-100 max-w-lg mx-auto">
            <FiAlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h3 className="text-sm sm:text-[15px] font-bold text-red-800">System Error</h3>
            <p className="text-xs sm:text-[12px] text-red-600 mt-1">{errorMessage}</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="p-10 sm:p-16 text-center bg-white rounded-2xl border border-slate-200 shadow-sm max-w-2xl mx-auto">
            <FiLayers className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <h3 className="text-sm sm:text-[16px] font-bold text-[#0F172A]">No Arrays Found</h3>
            <p className="text-xs sm:text-[13px] text-slate-500 mt-1 font-medium">Try adjusting your search criteria or resetting filters.</p>
          </div>
        ) : (
          
          /* 🌟 PROJECTS GRID 🌟 */
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project, idx) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: idx * 0.05 }}
                  key={project.id}
                  className="flex flex-col rounded-2xl overflow-hidden border border-slate-200/80 bg-white group hover:shadow-[0_15px_30px_rgba(13,63,127,0.08)] hover:border-[#F7941D]/40 transition-all duration-300 justify-between"
                >
                  <div>
                    {/* Image Box */}
                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                      {project.image ? (
                        <img 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                          src={project.image} 
                          alt={project.title}
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                          <FiSun className="w-12 h-12 opacity-30" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/80 via-transparent to-transparent pointer-events-none" />
                      
                      {/* Top Badges */}
                      <span className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 bg-[#0D3F7F]/90 backdrop-blur-md px-2 py-1 sm:px-2.5 rounded-md text-[9px] font-bold tracking-widest text-[#FFC107] uppercase shadow-sm border border-white/10">
                        {project.tag}
                      </span>
                      <span className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 bg-[#F7941D] text-white px-2 py-1 sm:px-2.5 rounded-md text-[9px] font-bold uppercase tracking-widest shadow-sm">
                        {project.status}
                      </span>
                    </div>

                    {/* Content Box */}
                    <div className="p-4 sm:p-5 space-y-3">
                      <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-[11px] font-bold text-slate-400 flex-wrap">
                        <span className="flex items-center gap-1"><FiMapPin className="text-[#F7941D]" /> {project.location}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                        <span className="flex items-center gap-1"><FiCalendar className="text-[#0D3F7F]" /> {project.completion_date || 'Ongoing'}</span>
                      </div>
                      
                      <h3 className="text-[15px] sm:text-[16px] font-black text-[#0F172A] group-hover:text-[#0D3F7F] transition-colors duration-300 line-clamp-1">
                        {project.title}
                      </h3>
                      
                      {/* Metric Mini-Grid */}
                      <div className="pt-3 border-t border-slate-100 grid grid-cols-2 gap-2.5 sm:gap-3 mt-2">
                        <div className="bg-[#F0F7FF] rounded-lg p-2 border border-[#1E5CB3]/10">
                          <span className="text-[9px] uppercase tracking-widest text-[#0D3F7F] font-bold block mb-0.5">Capacity</span>
                          <span className="text-xs sm:text-[13px] font-black text-[#0F172A] flex items-center gap-1">
                            <FiZap className="text-[#F7941D] w-3 h-3 shrink-0"/> {project.capacity}
                          </span>
                        </div>
                        <div className="bg-[#FFF9F0] rounded-lg p-2 border border-[#F7941D]/10">
                          <span className="text-[9px] uppercase tracking-widest text-[#F7941D] font-bold block mb-0.5">Est. Savings</span>
                          <span className="text-xs sm:text-[13px] font-black text-[#0F172A] flex items-center gap-1">
                            <FiTrendingUp className="text-[#10B981] w-3 h-3 shrink-0"/> {project.savings}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                    <button 
                      onClick={() => setSelectedProject(project)}
                      className="w-full py-2.5 px-4 rounded-xl border border-slate-200 bg-white hover:bg-[#0D3F7F] hover:border-[#0D3F7F] text-xs sm:text-[12px] font-bold text-[#0F172A] hover:text-white transition-all duration-300 flex items-center justify-center gap-2 group/btn cursor-pointer shadow-sm touch-manipulation"
                    >
                      <span>View Specifications</span>
                      <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform text-[#F7941D]"/>
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </section>

      {/* 🌟 3. PROJECT DETAILS MODAL (High-End Blueprint Style) 🌟 */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-[#050B14]/80 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-[#0A1326] rounded-2xl sm:rounded-3xl border border-[#1E5CB3]/30 shadow-[0_20px_60px_rgba(0,0,0,0.5)] max-w-2xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto hide-scrollbar p-5 sm:p-8 space-y-5 sm:space-y-6 text-white relative"
            >
              <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-[#F7941D]/10 rounded-full blur-[60px] sm:blur-[80px] pointer-events-none" />

              <div className="flex items-start justify-between gap-3 sm:gap-4 relative z-10">
                <div className="min-w-0 flex-1">
                  <span className="px-2.5 py-1 text-[9px] font-black uppercase tracking-widest rounded-md bg-[#F7941D] text-white shadow-[0_0_10px_rgba(247,148,29,0.3)]">
                    {selectedProject.tag}
                  </span>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-white mt-3 leading-tight">
                    {selectedProject.title}
                  </h2>
                  <p className="text-[11px] sm:text-[12px] text-slate-400 font-medium mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1"><FiMapPin className="text-[#FFC107]" /> {selectedProject.location}</span>
                    <span>• Status: <span className="text-[#10B981]">{selectedProject.status}</span></span>
                  </p>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="p-2 sm:p-2.5 rounded-xl bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-white/10 hover:border-red-500/30 transition-all cursor-pointer shrink-0 touch-manipulation"
                  aria-label="Close"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              {selectedProject.image && (
                <div className="rounded-xl sm:rounded-2xl overflow-hidden aspect-[16/9] border border-white/10 relative z-10 shadow-lg">
                  <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-full object-cover" />
                </div>
              )}

              <div className="relative z-10">
                <h4 className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-[#FFC107] mb-2">Project Overview</h4>
                <p className="text-xs sm:text-[13px] text-slate-300 font-medium leading-relaxed">
                  {selectedProject.description}
                </p>
              </div>

              <div className="p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 space-y-4 relative z-10 backdrop-blur-sm">
                <h4 className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-[#60A5FA] flex items-center gap-2">
                  <FiCpu className="text-[#F7941D]" />
                  Equipment Matrix
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-[12px]">
                  <div className="border-l-2 border-[#1E5CB3] pl-3">
                    <span className="text-slate-400 block text-[9px] sm:text-[10px] uppercase font-bold tracking-wider mb-0.5">PV Modules</span>
                    <strong className="text-white text-xs sm:text-sm">{selectedProject.panels || 'High-Efficiency Monocrystalline'}</strong>
                  </div>
                  <div className="border-l-2 border-[#1E5CB3] pl-3">
                    <span className="text-slate-400 block text-[9px] sm:text-[10px] uppercase font-bold tracking-wider mb-0.5">Inverter Unit</span>
                    <strong className="text-white text-xs sm:text-sm">{selectedProject.inverter || 'Smart Grid Inverter'}</strong>
                  </div>
                  <div className="border-l-2 border-[#F7941D] pl-3">
                    <span className="text-slate-400 block text-[9px] sm:text-[10px] uppercase font-bold tracking-wider mb-0.5">System Capacity</span>
                    <strong className="text-[#F7941D] text-[13px] sm:text-[14px]">{selectedProject.capacity}</strong>
                  </div>
                  <div className="border-l-2 border-[#10B981] pl-3">
                    <span className="text-slate-400 block text-[9px] sm:text-[10px] uppercase font-bold tracking-wider mb-0.5">Annual Savings</span>
                    <strong className="text-[#10B981] text-[13px] sm:text-[14px]">{selectedProject.savings}</strong>
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3 pt-4 border-t border-white/10 relative z-10">
                <button onClick={() => setSelectedProject(null)} className="px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white font-bold text-xs sm:text-[12px] border border-white/10 transition-all cursor-pointer text-center touch-manipulation">
                  Close Preview
                </button>
                <Link to="/contact" className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white font-bold text-xs sm:text-[12px] shadow-[0_0_15px_rgba(247,148,29,0.3)] hover:shadow-[0_0_25px_rgba(247,148,29,0.5)] transition-all text-center touch-manipulation">
                  Request Similar Layout
                </Link>
              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🌟 4. COMPACT EXECUTION WORKFLOW 🌟 */}
      <section className="py-12 sm:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-200/50">
        <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#0F172A]">
            Execution Framework
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {timelineSteps.map((step, i) => (
            <div key={step.step} className="flex flex-col items-center text-center p-3.5 sm:p-4 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-[#F7941D]/40 hover:shadow-md transition-all group">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#F0F7FF] border border-[#1E5CB3]/10 flex items-center justify-center text-[#0D3F7F] font-black text-xs sm:text-[13px] group-hover:bg-[#F7941D] group-hover:text-white group-hover:border-[#F7941D] transition-colors mb-2.5 sm:mb-3">
                {step.step}
              </div>
              <h3 className="text-xs sm:text-[13px] font-bold text-[#0F172A] mb-1">{step.title}</h3>
              <p className="text-[10px] sm:text-[11px] text-slate-500 font-medium leading-tight">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 🌟 5. HARDWARE STACK (Dark Matrix) 🌟 */}
      <section className="py-12 sm:py-16 bg-[#050B14] text-white relative overflow-hidden border-t border-[#1E5CB3]/20">
        <div className="absolute top-0 left-1/4 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] bg-[#1E5CB3]/10 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-[#F7941D]/10 border border-[#F7941D]/20 px-3 py-1 rounded-full mb-3">
              <FiCpu className="w-3.5 h-3.5 text-[#F7941D]" />
              <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#FFC107] uppercase">Hardware Stack</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
              Tier-1 Certified Components
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {technologies.map((tech, i) => (
              <div key={tech.name} className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#F7941D]/30 transition-all flex items-start gap-3 sm:gap-4">
                <div className="p-2.5 bg-[#0D3F7F]/40 text-[#FFC107] rounded-xl border border-[#1E5CB3]/30 shrink-0">
                  <tech.icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[13px] sm:text-[14px] font-bold text-white mb-1 leading-tight">{tech.name}</h3>
                  <p className="text-[11px] text-slate-400 font-medium leading-relaxed">{tech.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hide Scrollbar for filters */}
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
}